import { useMemo } from "react"
import { useMeetingsStore } from "@/features/meetings/store/meetings.store"
import { useWebinarStore } from "@/features/webinar/store/webinar.store"
import type { CalendarEvent } from "../models/calendar.model"
import { joinLockedUntil, joinPathId } from "@/features/meetings/utils/recurrence"

/**
 * Joins the persisted meetings + webinars stores into the unified
 * `CalendarEvent` shape used by the Day / Week / Month / Year views.
 *
 * Everything is derived; the Calendar UI stays unaware of the underlying
 * feature stores. When a real backend lands, this is the single seam to
 * swap out — the views consume `CalendarEvent[]` and nothing else.
 */

const pad = (n: number): string => String(n).padStart(2, "0")

const formatHM = (date: Date): string =>
  `${pad(date.getHours())}:${pad(date.getMinutes())}`

const toCalendarStatus = (status: string): CalendarEvent["status"] => {
  if (status === "ongoing") return "ongoing"
  if (status === "upcoming" || status === "draft") return "upcoming"
  // cancelled / expired / completed → completed
  return "completed"
}

export function useCalendarEvents(): CalendarEvent[] {
  const meetings = useMeetingsStore((s) => s.meetings)
  const webinars = useWebinarStore((s) => s.webinars)

  return useMemo<CalendarEvent[]>(() => {
    const meetingEvents: CalendarEvent[] = meetings.map((m) => {
      const start = new Date(m.startTimeMs)
      const end = new Date(m.endTimeMs)
      const participantCount = m.participants?.length
        ? m.participants.length
        : Math.max(1, m.attendees?.length ?? m.seats ?? 1)

      return {
        id: `meeting:${m.id}`,
        title: m.title,
        description: m.description,
        date: start,
        startTime: formatHM(start),
        endTime: formatHM(end),
        type: "meeting",
        participants: participantCount,
        status: toCalendarStatus(m.status),
        location: m.timezone,
        entityKind: "meeting",
        entityId: m.id,
        joinId: joinPathId(m),
        joinLockedUntilMs: joinLockedUntil(m),
        hostName: m.host,
      }
    })

    const webinarEvents: CalendarEvent[] = webinars.map((w) => {
      const start = new Date(w.startTimeMs)
      const end = new Date(w.endTimeMs)
      const participantCount = w.registrants?.length || w.maxRegistrants || 0

      return {
        id: `webinar:${w.id}`,
        title: w.title,
        description: w.description,
        date: start,
        startTime: formatHM(start),
        endTime: formatHM(end),
        type: "webinar",
        participants: participantCount,
        status: toCalendarStatus(w.status),
        location: w.timezone,
        entityKind: "webinar",
        entityId: w.id,
        hostName: w.hostName,
      }
    })

    return [...meetingEvents, ...webinarEvents].sort(
      (a, b) => a.date.getTime() - b.date.getTime(),
    )
  }, [meetings, webinars])
}
